<template>
  <main class="join">
    <h1 class="join__title">회원가입</h1>
    <hr class="join__divider" />

    <ol class="steps" aria-label="회원가입 진행 단계">
      <li class="step is-done">
        <div class="step__icon"><i class="fa-regular fa-id-badge" aria-hidden="true"></i></div>
        <div class="step__text"><span class="step__sub">STEP 01</span><strong>약관동의</strong></div>
      </li>
      <li class="step is-active">
        <div class="step__icon"><i class="fa-regular fa-pen-to-square" aria-hidden="true"></i></div>
        <div class="step__text"><span class="step__sub">STEP 02</span><strong>정보입력</strong></div>
      </li>
      <li class="step">
        <div class="step__icon"><i class="fa-regular fa-circle-check" aria-hidden="true"></i></div>
        <div class="step__text"><span class="step__sub">STEP 03</span><strong>가입완료</strong></div>
      </li>
    </ol>

    <p class="join__note">“별표(<span class="req">*</span>) 표시 항목은 필수 입력 사항입니다.”</p>

    <section class="form">

      <!-- 성명 -->
      <div class="row">
        <label class="row__label" for="name">성명<span class="req">*</span></label>
        <div class="row__field">
          <input id="name" type="text" placeholder="홍길동" v-model.trim="form.userName" />
        </div>
      </div>

      <!-- 성별 -->
      <div class="row">
        <span class="row__label">성별<span class="req">*</span></span>
        <div class="row__field">
          <label class="radio">
            <input type="radio" name="gender" value="M" v-model="form.gender" />
            <span>남</span>
          </label>
          <label class="radio">
            <input type="radio" name="gender" value="F" v-model="form.gender" />
            <span>여</span>
          </label>
        </div>
      </div>

      <!-- 휴대전화 -->
      <div class="row">
        <label class="row__label" for="mobile">휴대전화<span class="req">*</span></label>
        <div class="row__field">
          <input id="mobile" type="tel" placeholder="010-1234-5678" v-model.trim="form.phone" />
        </div>
      </div>

      <!-- 이메일 -->
      <div class="row">
        <label class="row__label" for="idEmail">ID : (이메일 주소)<span class="req">*</span></label>
        <div class="row__field">
          <div class="input-with-btn">
            <input id="idEmail" type="email" placeholder="이메일을 입력해주세요" v-model.trim="form.userId"
              :disabled="emailVerified" />
            <button type="button" class="btn btn--line" @click="requestEmailCode" :disabled="isSending || emailVerified"
              :aria-expanded="emailVerifyOpen ? 'true' : 'false'" aria-controls="email-verify-panel">
              {{ isSending ? '전송 중…' : (emailVerified ? '인증 완료' : '이메일 인증') }}
            </button>
          </div>

          <div id="email-verify-panel" class="verify-panel" v-show="emailVerifyOpen">
            <label class="verify-label" for="verifyCode">인증번호</label>
            <div class="verify-row">
              <input id="verifyCode" type="text" inputmode="numeric" placeholder="숫자 5자리" maxlength="5"
                ref="verifyInputRef" v-model.trim="form.emailCode" :disabled="emailVerified" />
              <button type="button" class="btn btn--primary" @click="confirmEmailCode"
                :disabled="isVerifying || emailVerified" :aria-busy="isVerifying ? 'true' : 'false'">
                {{ isVerifying ? '확인 중…' : (emailVerified ? '인증 완료' : '확인') }}
              </button>
            </div>

            <button class="resend" type="button" @click="resendCode"
              :disabled="resendCooldown > 0 || isSending || isResending || emailVerified"
              :aria-disabled="resendCooldown > 0 || isSending || isResending || emailVerified ? 'true' : 'false'"
              :title="resendCooldown > 0 ? `${formatTime(resendCooldown)} 후 재전송 가능` : '인증번호 재전송'">
              <template v-if="isResending">재전송 중…</template>
              <template v-else-if="resendCooldown > 0">인증번호 재전송 ({{ formatTime(resendCooldown) }})</template>
              <template v-else>인증번호 재전송</template>
            </button>

            <!-- <p v-if="emailError" class="verify-error" role="alert">{{ emailError }}</p> -->
          </div>
        </div>
      </div>

      <!-- 비밀번호 -->
      <div class="row">
        <label class="row__label" for="pw">비밀번호<span class="req">*</span></label>
        <div class="row__field">
          <input id="pw" type="password" placeholder="영문/숫자 포함 8자 이상" v-model.trim="form.password" />
        </div>
      </div>

      <!-- 비밀번호 재확인 -->
      <div class="row">
        <label class="row__label" for="pw2">비밀번호 재확인<span class="req">*</span></label>
        <div class="row__field">
          <input id="pw2" type="password" placeholder="비밀번호를 다시 입력하세요" v-model.trim="form.confirmPassword" />

          <!-- ✅ 상태 메시지 -->
          <p v-if="passwordMatch === true" class="pw-ok">
            ✅ 비밀번호가 일치합니다.
          </p>
          <p v-else-if="passwordMatch === false" class="pw-error">
            ⚠ 비밀번호가 일치하지 않습니다.
          </p>
        </div>
      </div>

      <!-- 회원 구분 -->
      <div class="row">
        <span class="row__label">회원 구분<span class="req">*</span></span>
        <div class="row__field">
          <label class="radio">
            <input type="radio" name="memberType" value="PERSONAL" v-model="form.memberType" />
            <p>개인</p>
          </label>
          <label class="radio">
            <input type="radio" name="memberType" value="CORP" v-model="form.memberType" />
            <p>법인</p>
          </label>
        </div>
      </div>


      <!-- 회사명 (법인만) -->
      <div class="row">
        <label class="row__label" for="company">회사명</label>
        <div class="row__field">
          <input id="company" type="text" placeholder="회사/기관명" v-model.trim="form.orgName" :disabled="isCorpDisabled" />
        </div>
      </div>

      <!-- 직급 (법인만 필수) -->
      <div class="row">
        <label class="row__label" for="title">
          직급<span v-if="form.memberType === 'CORP'" class="req">*</span>
        </label>
        <div class="row__field">
          <input id="title" type="text" placeholder="예) 과장, 매니저" v-model.trim="form.position"
            :disabled="isCorpDisabled" />
        </div>
      </div>



      <!-- 연락처(사무실) (법인만 필수) -->
      <div class="row">
        <label class="row__label" for="officeTel">
          연락처(사무실)<span v-if="form.memberType === 'CORP'" class="req">*</span>
        </label>
        <div class="row__field">
          <input id="officeTel" type="tel" placeholder="02-123-4567" v-model.trim="form.orgPhone"
            :disabled="isCorpDisabled" />
        </div>
      </div>

      <!-- 주소 -->
      <div class="row">
        <label class="row__label" for="roadAddress">주소</label>
        <div class="row__field">
          <div class="input-with-btn" style="margin-bottom:8px;">
            <input id="zonecode" type="text" placeholder="우편번호" v-model="form.zoneCode" readonly />
            <button type="button" class="btn btn--line" @click="openDaumPostcode">우편번호 찾기</button>
          </div>
          <input id="roadAddress" type="text" placeholder="도로명 주소" v-model="form.address" readonly
            style="margin-bottom:8px;" />
          <input id="extraAddress" type="text" placeholder="참고항목" v-model="form.extraAddress" readonly
            style="margin-bottom:8px;" />
          <input id="detailAddress" type="text" placeholder="상세주소 입력" v-model.trim="form.detailAddress"
            ref="detailRef" />
        </div>
      </div>

      <!-- 액션 -->
      <div class="actions">
        <button type="button" class="btn btn--ghost" @click="go('/')">취소</button>
        <button type="button" class="btn btn--primary" @click="onSubmit">회원가입</button>
      </div>
    </section>
  </main>
</template>


<script setup>
import { useRouter } from 'vue-router'
import { ref, reactive, computed, watch, nextTick, onUnmounted } from 'vue'
import { sendJoinNum, checkJoinNum, reSendJoinNum, joinUser } from '@/api/user'

const router = useRouter()

const form = reactive({
  // 이메일/인증
  userId: '',
  emailCode: '',       // 인증번호(5자리)

  // 기본정보
  password: '',
  confirmPassword: '',
  memberType: '', // 'personal' | 'corp'
  userName: '',
  gender: '',

  // 회사/연락처
  orgName: '',
  position: '',
  phone: '',           // 휴대전화
  orgPhone: '',        // 사무실 연락처

  // 주소
  zoneCode: '',
  address: '',
  extraAddress: '',
  detailAddress: '',
})

/* =========================
 * UI 상태
 * ========================= */
const emailVerifyOpen = ref(false)
const emailVerified = ref(false)
const isSending = ref(false)
const isVerifying = ref(false)
const isResending = ref(false)
const emailError = ref('')
const resendCooldown = ref(0)
let resendTimer = null
const isLoading = ref(false)
const passwordMatch = ref(null)

const verifyInputRef = ref(null)
const detailRef = ref(null)

const userId = ref('')
/* =========================
 * 파생 상태/유틸
 * ========================= */
const isCorpDisabled = computed(() => form.memberType !== 'CORP')
const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const CODE_LENGTH = 5
function formatTime(seconds) {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}
function startResendCooldown(sec) {
  stopResendCooldown()
  resendCooldown.value = sec
  resendTimer = window.setInterval(() => {
    resendCooldown.value -= 1
    if (resendCooldown.value <= 0) stopResendCooldown()
  }, 1000)
}
function stopResendCooldown() {
  if (resendTimer) {
    clearInterval(resendTimer)
    resendTimer = null
  }
}
onUnmounted(stopResendCooldown)

/* =========================
 * 이메일 인증
 * ========================= */
async function requestEmailCode() {
  emailError.value = ''

  if (!emailRe.test(form.userId)) {
    alert('이메일 형식이 올바르지 않습니다.')
    return
  }

  // ✅ 클릭 즉시 열고 포커스 먼저
  emailVerifyOpen.value = true
  form.emailCode = ''
  await nextTick()
  verifyInputRef.value?.focus()

  try {
    isSending.value = true
    const res = await sendJoinNum({ email: form.userId })

    if (res?.result === 'ok') {
      startResendCooldown(600) // 10분
      alert(res?.resultMsg || '인증번호를 발송했습니다. 메일함을 확인하세요.')
    } else {
      const msg = res?.resultMsg || res?.message || '인증번호 발송에 실패했습니다.'
      emailError.value = msg
      // 패널은 열린 상태 유지(재시도 편의)
    }
  } catch (e) {
    console.error(e)
    emailError.value = '네트워크 오류가 발생했습니다.'
  } finally {
    isSending.value = false
  }
}


async function confirmEmailCode() {
  emailError.value = ''
  if (!emailRe.test(form.userId)) { alert('이메일 형식이 올바르지 않습니다.'); return }
  if (!new RegExp(`^\\d{${CODE_LENGTH}}$`).test(form.emailCode.trim())) {
    alert(`인증번호 ${CODE_LENGTH}자리를 정확히 입력해주세요.`)
    return
  }

  try {
    isVerifying.value = true
    const res = await checkJoinNum({ email: form.userId, joinNum: form.emailCode.trim() })
    if (res?.result === 'ok') {
      emailVerified.value = true
      userId.value = res.userId
      stopResendCooldown()
      alert(res?.resultMsg || '이메일 인증이 완료되었습니다.')
    } else {
      const msg = res?.resultMsg || res?.message || '인증번호가 올바르지 않습니다.'
      emailError.value = msg
      alert(msg)
    }
  } catch (e) {
    console.error(e)
    alert('네트워크 오류가 발생했습니다.')
  } finally {
    isVerifying.value = false
  }
}

async function resendCode() {
  if (emailVerified.value) return
  if (resendCooldown.value > 0 || isSending.value || isResending.value) return
  if (!emailRe.test(form.userId)) { alert('이메일 형식이 올바르지 않습니다.'); return }

  try {
    isResending.value = true
    const res = await reSendJoinNum({ email: form.userId })
    if (res?.result === 'ok') {
      emailVerifyOpen.value = true
      form.emailCode = ''
      nextTick(() => verifyInputRef.value?.focus())
      startResendCooldown(600) // 10분
      alert(res?.resultMsg || '인증번호를 재전송했습니다. 메일함을 확인하세요.')
    } else {
      const msg = res?.resultMsg || res?.message || '인증번호 재전송에 실패했습니다.'
      emailError.value = msg
      alert(msg)
    }
  } catch (e) {
    console.error(e)
    alert('네트워크 오류가 발생했습니다.')
  } finally {
    isResending.value = false
  }
}

/* =========================
 * 주소 검색 (다음 우편번호)
 * ========================= */
function loadDaumPostcodeScript() {
  return new Promise((resolve, reject) => {
    if (window.daum?.Postcode) { resolve(); return }
    const s = document.createElement('script')
    s.src = '//t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js'
    s.onload = () => resolve()
    s.onerror = () => reject(new Error('Daum Postcode script load error'))
    document.head.appendChild(s)
  })
}
async function openDaumPostcode() {
  await loadDaumPostcodeScript()
  new window.daum.Postcode({
    oncomplete: (data) => {
      const addr = (data.userSelectedType === 'R') ? data.roadAddress : data.jibunAddress
      let extra = ''
      if (data.userSelectedType === 'R') {
        if (data.bname && /[동|로|가]$/.test(data.bname)) extra += data.bname
        if (data.buildingName && data.apartment === 'Y') extra += (extra ? ', ' : '') + data.buildingName
      }
      form.zoneCode = data.zonecode
      form.address = addr
      form.extraAddress = extra ? `(${extra})` : ''
      requestAnimationFrame(() => detailRef.value?.focus())
    }
  }).open()
}

/* =========================
 * 폼 검증 + 제출
 * ========================= */
function missing(label) { alert(`${label} 입력을 완료해주세요.`) }

async function onSubmit() {

  // 기본 검증
  if (!emailVerified.value) { alert('이메일 인증을 완료해 주세요.'); return }
  if (!form.password || form.password.length < 8) { alert('비밀번호는 8자 이상이어야 합니다.'); return }
  if (form.password !== form.confirmPassword) { alert('비밀번호가 일치하지 않습니다.'); return }
  if (!form.memberType) { missing('회원 구분'); return }
  if (!form.userName) { missing('성명'); return }
  if (!form.gender) { missing('성별'); return }
  if (!form.phone) { missing('휴대전화'); return }
  if (form.memberType === 'CORP') {
    if (!form.orgName) { missing('회사명'); return }
    if (!form.position) { missing('직급'); return }
    if (!form.orgPhone) { missing('연락처(사무실)'); return }
  }

  if (!confirm('등록하시겠습니까?')) return


  isLoading.value = true
  try {
    // 서버 파라미터 매핑
    const param = {
      userId: userId.value,
      userName: form.userName,
      passwordHash: form.password,
      userType: form.memberType,
      corporateId: null,
      orgName: form.orgName,
      position: form.position,
      phone: form.phone,
      orgPhone: form.orgPhone,
      zoneCode: form.zoneCode,
      address: form.address,
      extraAddress: form.extraAddress,
      detailAddress: form.detailAddress,
      gender: form.gender
    }

    const resultVo = await joinUser(param)
    if (resultVo?.result === 'ok') {
      //  alert('회원가입이 완료되었습니다.')
      sessionStorage.setItem('joinProgress', '2')
      router.push('/join-step3')
      // 완료 화면에 보여줄 최소 정보 저장
      const joined = {
        email: form.userId,                           // 아이디(이메일)
        userName: form.userName,                      // 성명
        createdAt: resultVo?.data?.createdAt          // API가 주면 사용
          || new Date().toISOString(),        // 없으면 현재 시각
        createdAtText: resultVo?.data?.createdAtText  // API가 텍스트를 주면 그대로 사용
      }
      sessionStorage.setItem('joinResult', JSON.stringify(joined))
      sessionStorage.setItem('joinProgress', '2')
      alert('회원가입이 완료되었습니다.')
      router.push('/join-step3')
    } else {
      alert(resultVo?.resultMsg || '등록 중 오류가 발생했습니다.')
    }
  } catch (e) {
    console.error('오류 발생:', e)
    alert('서버 요청 중 문제가 발생했습니다.')
  } finally {
    isLoading.value = false
  }
}

/* =========================
 * 기타
 * ========================= */
function resetCorpFields() {
  form.orgName = ''
  form.position = ''
  form.orgPhone = ''
}
watch(() => form.memberType, (nv, ov) => {
  if (nv === 'PERSONAL' && ov === 'CORP') resetCorpFields()
})

// ✅ 비밀번호 일치 실시간 검증
watch(
  () => [form.password, form.confirmPassword],
  ([pw, pw2]) => {
    if (!pw && !pw2) passwordMatch.value = null
    else if (!pw2) passwordMatch.value = null
    else passwordMatch.value = pw === pw2
  }
)
function go(to) { router.push(to) }

// 내보내서 템플릿에서 쓰는 함수/값
defineExpose({ formatTime })
</script>


<style scoped>
/* 시스템 폰트(상업적 무료) */
.join {
  font-family: system-ui, -apple-system, "Segoe UI", "Noto Sans KR",
    "Apple SD Gothic Neo", "Malgun Gothic", Arial, sans-serif;
  padding: 28px 16px 60px;
  background: #f8fafc;
  color: #0f172a;
}

/* 제목/구분선 (약관동의와 동일) */
.join__title {
  text-align: center;
  font-size: 2rem;
  font-weight: 900;
  letter-spacing: -.2px;
  margin: 8px 0 12px;
}

.join__divider {
  max-width: 760px;
  margin: 0 auto 24px;
  border: 0;
  border-top: 1px solid #e5e7eb;
}

/* 스텝 (약관동의와 동일) */
.steps {
  list-style: none;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
  max-width: 760px;
  margin: 0 auto 24px;
  padding: 0;
}

.step {
  display: grid;
  grid-template-columns: 44px 1fr;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  color: #334155;
}

.step__icon {
  width: 44px;
  height: 44px;
  border-radius: 999px;
  display: grid;
  place-items: center;
  background: #f1f5f9;
  color: #334155;
}

.step__text {
  display: grid;
  gap: 2px;
}

.step__sub {
  font-size: .75rem;
  color: #64748b;
}

.step strong {
  font-size: .95rem;
  font-weight: 800;
  color: #0f172a;
}

.step.is-active {
  border-color: #c7d2fe;
  box-shadow: 0 6px 18px rgba(17, 24, 39, .06);
}

.step.is-active .step__icon {
  background: #1d2088;
  color: #fff;
}

.step.is-done {
  opacity: .85;
}

/* 필수 안내 */
.join__note {
  max-width: 760px;
  margin: 0 auto 12px;
  font-size: .95rem;
  color: #111827;
}

.req {
  color: #dc2626;
}

.input-with-btn {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 8px;
}

/* row__field input 기본 톤을 이미 쓰고 있다면 그대로 사용됩니다.
   필요 시 높이/폰트 일치: */
.input-with-btn input {
  height: 52px;
  padding: 0 16px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  font-size: 16px;
}

/* 폼 카드: 약관 페이지 카드 톤과 일치 */
.form {
  max-width: 760px;
  margin: 0 auto;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px 16px 20px;
  box-shadow: 0 10px 30px rgba(17, 24, 39, .06);
}

/* 행 레이아웃: 좌측 라벨, 우측 필드 (가입폼과 동일 그리드) */
.row {
  display: grid;
  grid-template-columns: 200px 1fr;
  /* gap: 14px; */
  align-items: start;
  padding: 12px 6px;
  border-top: 1px solid #eef2f7;
  margin-right: 16px;
}

.row:first-of-type {
  border-top: 0;
}

.row__label {
  margin-top: 12px;
  color: #111827;
  font-weight: 700;
  font-size: .95rem;
}

.row__field {
  position: relative;
}

/* 인풋 스타일: 로그인/회원가입과 동일 톤 */
.row__field input {
  width: 90%;
  height: 52px;
  padding-left: 16px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  font-size: 16px;
  color: #0f172a;
  background: #fff;
  transition: border-color .15s ease, box-shadow .15s ease;
}

.row__field input::placeholder {
  color: #94a3b8;
}

.row__field input:focus {
  outline: none;
  border-color: #a5b4fc;
  box-shadow: 0 0 0 4px rgba(99, 102, 241, .15);
}

/* 성별 라디오 */
.radio {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 52px;
  color: #0f172a;
}

.radio p {
  white-space: nowrap;
}

/* 액션 버튼 (약관 페이지 버튼 톤과 동일) */
.actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-top: 16px;
}

.btn {

  height: 54px;
  font-size: 16px;
  font-weight: 800;
  border-radius: 12px;
  border: 1px solid transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.btn--primary {
  background: #1d2088;
  color: #fff;
}

.btn--primary:hover {
  background: #220e52;
}

/* 버튼들 */
.btn {
  height: 52px;
  padding: 0 16px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 15px;
  cursor: pointer;
  border: 1px solid transparent;
}

.btn--line {
  background: #fff;
  color: #1d2088;
  border-color: #1d2088;
}

.btn--line:hover {
  background: rgba(29, 32, 136, .06);
}

.btn--primary {
  background: #1d2088;
  color: #fff;
}

.btn--primary:hover {
  background: #220e52;
}

/* 인증 패널 (부드럽게 펼침 느낌) */
.verify-panel {
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px dashed #e5e7eb;
}

.verify-label {
  display: block;
  margin: 6px 0 6px;
  font-weight: 700;
  font-size: .95rem;
  color: #111827;
}

.verify-row {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 8px;
}

.verify-row input {
  height: 52px;
  padding: 0 16px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  font-size: 16px;
}

/* 인증번호 재전송 버튼 */
.resend {
  margin-top: 8px;
  background: transparent;
  border: 0;
  color: #1d2088;
  font-size: 14px;
  font-weight: 600;
  text-decoration: underline;
  cursor: pointer;
  padding: 0;
}

.resend:hover {
  color: #220e52;
  text-decoration: none;
}

/* 모바일 */
@media (max-width: 640px) {
  .steps {
    grid-template-columns: 1fr;
  }

  .row {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .row__label {
    margin-top: 0;
  }
}

/* 폰트는 상업적 무료 + Noto Sans KR 고정 */
.join {
  font-family: 'Noto Sans KR', sans-serif;
  padding: 28px 16px 60px;
  background: #f8fafc;
  color: #0f172a;
}

/* 타이틀 규칙 */
.join__title {
  text-align: center;
  font-size: 1.625rem;
  /* 요청값 */
  font-weight: 700;
  /* 요청값 */
  letter-spacing: -.2px;
  margin: 8px 0 12px;
}

/* 본문 p 공통 */
p {
  font-size: 0.95rem;
}

/* 나머지 기존 스타일은 그대로 사용 (생략) */

/* disabled 시 톤다운 */
.row__field input:disabled {
  background: #f3f4f6;
  color: #9ca3af;
  border-color: #e5e7eb;
  cursor: not-allowed;
}

.pw-ok {
  color: #2e7d32;
  font-size: 0.85rem;
  margin-top: 6px;
}
.pw-error {
  color: #d32f2f;
  font-size: 0.85rem;
  margin-top: 6px;
}

/* 입력 상태 테두리 */
.row .row__field input.is-ok {
  outline: 2px solid #22c55e; /* 초록 */
  outline-offset: 2px;
}
.row .row__field input.is-error {
  outline: 2px solid #ef4444; /* 빨강 */
  outline-offset: 2px;
}

/* 상태 문구 */
.pw-msg {
  font-size: 0.85rem;
  margin-top: 6px;
  font-family: 'Noto Sans KR', sans-serif;
}
.pw-msg.ok { color: #166534; }     /* 짙은 초록 */
.pw-msg.error { color: #b91c1c; }  /* 짙은 빨강 */


</style>
